import React, { useCallback, useMemo } from 'react';

import { useDispatch, useSelector, useStore } from 'react-redux';
import { ShopifyColorful } from '@picsio/icons';

import { syncProductToDraft } from '../../../store/products/syncProductToDraft';
import { syncSelectedVariantsToDraft } from '../../../store/products/syncSelectedVariantsToDraft';
import { syncSingleVariantToDraft } from '../../../store/products/syncSingleVariantToDraft';
import { createAndPublishListingFromMaster } from '../../../store/products/createAndPublishListingFromMaster';
import { getMasterProduct } from '../../../store/products/getMasterProduct';
import {
  allConnectedChannelsHaveLiveVariant,
} from '../../../store/products/helpers/listingSyncHelpers';
import {
  selectChannelOptions,
  selectProductListings,
  selectProductIsBusy,
  selectProductBusyLoadingText,
  selectMasterProduct,
  hasListingProductInMap,
} from '../../../store/selectors/productSelectors';
import localization from '../../../shared/strings';
import Logger from '../../../services/Logger';
import {
  hasActiveConnectedShopifyStore,
  hasConnectedShopifyStore,
  isSalesChannelDisconnected,
  SHOPIFY_CHANNEL_TYPE,
} from '../../teammates/views/Channels/helpers/salesChannelState';
import { showVariantsSelectionDialog } from '../VariantsSelectionDialog';
import { validatePublishUnmappedFieldsGuard } from '../helpers/runPublishUnmappedFieldsGuard';
import SYNC_SCENARIO from './constants/syncFlowConstants';
import {
  getChannelsWithDraft,
  getChannelsWithLive,
  getSyncScenario,
} from './helpers/syncFlowHelpers';
import createSyncMediaPrefetchRunner from './helpers/createSyncMediaPrefetchRunner';
import startSyncFlow from './helpers/startSyncFlow';
import {
  CHANNEL_SYNC_ACTION,
  resolveSyncChannelIds,
  splitChannelsByAction,
} from './helpers/syncFlowOrchestrator';

const logSyncCancelled = (actionName) => Logger.log('User', actionName || 'CreatedStageVersionCancelled');

const findProductById = (list, productId) => (
  list?.find(({ _id }) => String(_id) === String(productId))
);

const readMasterProductFromState = (productsState, productId) => (
  findProductById(productsState?.items, productId)
  || findProductById(productsState?.search?.items, productId)
  || {}
);

const needsMasterVariantsFetch = (productsState, productId) => {
  if (!productId) return false;

  const itemsProduct = findProductById(productsState?.items, productId);
  if (!itemsProduct) return true;
  if (!Array.isArray(itemsProduct.variants)) return true;
  return Boolean(itemsProduct.hasVariants && !itemsProduct.variants.length);
};

const executeSyncFromFlowState = ({
  flowState,
  createDraft,
  publishDirectly,
}) => {
  const {
    channelActionsByChannelId,
    masterVariantIds,
    isVariantSync,
    overrideExisting,
    publishingScope,
    productMediaByChannel,
    variantMediaByChannel,
    variantMediaStepCompleted,
    mediaAltTextByChannel,
  } = flowState;

  const draftMedia = {
    publishingScope,
    productMediaByChannel,
    variantMediaByChannel,
    isVariantSync,
    variantMediaStepCompleted,
    mediaAltTextByChannel,
  };

  const { draftChannels, publishChannels } = splitChannelsByAction(channelActionsByChannelId);

  if (draftChannels.length) {
    createDraft({
      channelIds: draftChannels,
      masterVariantIds,
      isSingleVariant: isVariantSync,
      overrideExisting,
      draftMedia,
    });
  }

  if (publishChannels.length) {
    publishDirectly({
      channelIds: publishChannels,
      masterVariantIds,
      overrideExisting,
      draftMedia,
    });
  }
};

const useSyncToChannel = ({ productId, variantId } = {}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const channelOptions = useSelector((state) => selectChannelOptions(state, productId));
  const product = useSelector((state) => selectMasterProduct(state, productId));
  const productListings = useSelector((state) => selectProductListings(state, productId));
  const team = useSelector((state) => state.user.team);
  const teamSalesChannels = useMemo(() => team?.salesChannels || [], [team?.salesChannels]);
  const isBusy = useSelector((state) => selectProductIsBusy(state, productId));
  const busyLoadingText = useSelector((state) => selectProductBusyLoadingText(state, productId));
  const connectedChannelOptions = channelOptions.filter(({ type }) => type !== 'master');
  const hasConnectedChannels = teamSalesChannels.length > 0;
  const isChannelDisconnected = hasConnectedShopifyStore(team) && !hasActiveConnectedShopifyStore(team);
  const isSyncDisabled = isBusy || isChannelDisconnected;
  const connectionLostTooltip = isChannelDisconnected
    ? localization.PRODUCT_INFO.publishedLayerConnectionLostTooltip
    : null;

  const teamChannelOptions = teamSalesChannels.map((salesChannel) => ({
    label: salesChannel.name,
    value: String(salesChannel._id),
    type: salesChannel.type,
    disconnected: isSalesChannelDisconnected(salesChannel),
    // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
    icon: salesChannel.type === SHOPIFY_CHANNEL_TYPE ? <ShopifyColorful /> : null,
  }));

  const disconnectedByChannelId = useMemo(
    () => new Map(
      teamSalesChannels.map((salesChannel) => [
        String(salesChannel._id),
        isSalesChannelDisconnected(salesChannel),
      ]),
    ),
    [teamSalesChannels],
  );

  const availableChannelOptions = Array.from(
    new Map(
      [
        ...connectedChannelOptions,
        ...teamChannelOptions,
      ].map((option) => [option.value, option]),
    ).values(),
  ).map((option) => ({
    ...option,
    disconnected: Boolean(
      option.disconnected ?? disconnectedByChannelId.get(String(option.value)),
    ),
  }));

  const createDraft = useCallback(({
    channelIds,
    masterVariantIds,
    isSingleVariant,
    draftMedia,
    overrideExisting,
  }) => {
    if (isSingleVariant && masterVariantIds?.length === 1) {
      dispatch(syncSingleVariantToDraft({
        productId,
        channelIds,
        masterVariantId: masterVariantIds[0],
        draftMedia,
        overrideExisting,
      }));
    } else if (masterVariantIds?.length) {
      dispatch(syncSelectedVariantsToDraft({
        productId,
        channelIds,
        masterVariantIds,
        draftMedia,
        overrideExisting,
      }));
    } else {
      dispatch(syncProductToDraft({
        productId,
        channelIds,
        draftMedia,
        overrideExisting,
      }));
    }

    Logger.log('User', 'CreatedStageVersionConfirm', { channelIds });
  }, [dispatch, productId]);

  const publishDirectly = useCallback(({
    channelIds,
    masterVariantIds,
    draftMedia,
    overrideExisting,
  }) => {
    dispatch(createAndPublishListingFromMaster({
      productId,
      channelIds,
      masterVariantIds,
      draftMedia,
      overrideExisting,
    }));
    Logger.log('User', 'PublishDirectlyConfirm', { channelIds });
  }, [dispatch, productId]);

  const handleCreateDraft = useCallback((
    selectedChannelIds,
    masterVariantIds,
    isSingleVariant = false,
    { overrideExisting = false, draftMedia = {} } = {},
  ) => {
    const createDrafts = (channelIds) => {
      if (!channelIds.length) {
        return;
      }

      createDraft({
        channelIds,
        masterVariantIds,
        isSingleVariant,
        draftMedia,
        overrideExisting,
      });
    };

    const channelsWithDraft = getChannelsWithDraft({
      selectedChannelIds,
      productListings,
    });

    if (channelsWithDraft.length > 0) {
      Logger.log('User', 'CreateStageVersionAlreadyListed', { channelIds: channelsWithDraft });
    }

    const channelIdsToSync = resolveSyncChannelIds({
      selectedChannelIds,
      conflictingChannelIds: channelsWithDraft,
      overrideExisting,
    });

    createDrafts(channelIdsToSync);
  }, [createDraft, productListings]);

  const handlePublishDirectly = useCallback((
    selectedChannelIds,
    masterVariantIds,
    { overrideExisting = false, draftMedia = {} } = {},
  ) => {
    const publishLive = (channelIds) => {
      if (!channelIds.length) {
        return;
      }

      publishDirectly({
        channelIds,
        masterVariantIds,
        draftMedia,
        overrideExisting,
      });
    };

    const channelsWithLive = getChannelsWithLive({
      selectedChannelIds,
      product,
      productsState: store.getState().products,
      productListings,
      masterVariantIds,
    });

    if (channelsWithLive.length > 0) {
      Logger.log('User', 'PublishDirectlyAlreadyLive', { channelIds: channelsWithLive });
    }

    const channelIdsToSync = resolveSyncChannelIds({
      selectedChannelIds,
      conflictingChannelIds: channelsWithLive,
      overrideExisting,
    });

    publishLive(channelIdsToSync);
  }, [product, productListings, publishDirectly, store]);

  const shouldShowVariantNote = useCallback(
    (channelId) => Boolean(variantId && !hasListingProductInMap(productListings, channelId)),
    [productListings, variantId],
  );

  const openSyncDialog = useCallback(async ({
    masterVariantIds,
    isVariant = false,
    syncAction,
  }) => {
    const productsState = store.getState().products;
    const variantIdsToPrefetch = masterVariantIds?.length
      ? masterVariantIds
      : (product.variants || []).map(({ _id }) => _id);
    const mediaPrefetch = createSyncMediaPrefetchRunner({
      dispatch,
      getLayerArgs: () => ({
        productId,
        product,
        productsState: store.getState().products,
        productListings,
        masterVariantIds: variantIdsToPrefetch,
      }),
    });

    const startPrefetch = async () => {
      try {
        await mediaPrefetch.prefetchRecordStates([syncAction]);
      } catch (error) {
        // Toast/log already happened in prefetchSyncMedia. The same
        // rejection stays in the runner so waitForSyncMedia can fail closed.
      }
    };
    startPrefetch();

    startSyncFlow({
      product,
      productId,
      productsState,
      productListings,
      channelOptions: availableChannelOptions,
      masterVariantIds,
      isVariantSync: isVariant,
      syncAction,
      shouldShowVariantNote,
      onCancel: logSyncCancelled,
      waitForSyncMedia: (flowState) => mediaPrefetch.prefetchRecordStates(
        Object.values(flowState.channelActionsByChannelId || {}),
      ),
      getProductsState: () => store.getState().products,
      validateUnmappedFields: ({ channelIds, onUnmappedDialogOpen }) => validatePublishUnmappedFieldsGuard({
        channelIds,
        dispatch,
        salesChannels: teamSalesChannels,
        onUnmappedDialogOpen,
        onAbort: logSyncCancelled,
      }),
      onComplete: (flowState) => {
        executeSyncFromFlowState({
          flowState,
          createDraft: ({
            channelIds,
            masterVariantIds: variantIds,
            isSingleVariant,
            overrideExisting,
            draftMedia,
          }) => {
            handleCreateDraft(channelIds, variantIds, isSingleVariant, {
              overrideExisting,
              draftMedia,
            });
          },
          publishDirectly: ({
            channelIds,
            masterVariantIds: variantIds,
            overrideExisting,
            draftMedia,
          }) => {
            handlePublishDirectly(channelIds, variantIds, {
              overrideExisting,
              draftMedia,
            });
          },
        });
      },
    });
  }, [
    availableChannelOptions,
    dispatch,
    handleCreateDraft,
    handlePublishDirectly,
    product,
    productId,
    productListings,
    shouldShowVariantNote,
    store,
    teamSalesChannels,
    variantId,
  ]);

  const startSingleVariantSync = useCallback((syncAction) => {
    openSyncDialog({ masterVariantIds: [variantId], isVariant: true, syncAction });
  }, [openSyncDialog, variantId]);

  const startChooseVariantsSync = useCallback((syncProduct = product, syncAction) => {
    const productForDialog = syncProduct || product;
    const productsState = store.getState().products;
    const connectedChannelIds = teamSalesChannels
      .filter((channel) => !isSalesChannelDisconnected(channel))
      .map((channel) => String(channel._id));
    const variantsOptional = allConnectedChannelsHaveLiveVariant({
      product: productForDialog,
      productListings,
      productsState,
      channelIds: connectedChannelIds,
    });
    const dialogStrings = variantsOptional
      ? localization.DIALOGS.PRODUCT_DIALOGS.CONNECT_CHANNEL.UPDATE_EXISTING
      : localization.DIALOGS.PRODUCT_DIALOGS.CONNECT_CHANNEL;

    showVariantsSelectionDialog({
      productTitle: productForDialog.title || '',
      variants: productForDialog.variants || [],
      strings: dialogStrings,
      variantsOptional,
      onConfirm: ({ variantIds, isProductSelected }) => {
        const masterVariantIds = isProductSelected && !variantIds?.length ? [] : variantIds;
        openSyncDialog({ masterVariantIds, syncAction });
      },
      onCancel: logSyncCancelled,
    });
  }, [openSyncDialog, product, productListings, store, teamSalesChannels]);

  const startProductSync = useCallback((syncAction) => {
    openSyncDialog({ syncAction });
  }, [openSyncDialog]);

  const runSyncScenario = useCallback((scenario, syncProduct, syncAction) => {
    if (scenario === SYNC_SCENARIO.SINGLE_VARIANT) {
      startSingleVariantSync(syncAction);
      return;
    }

    if (scenario === SYNC_SCENARIO.CHOOSE_VARIANTS) {
      startChooseVariantsSync(syncProduct, syncAction);
      return;
    }

    startProductSync(syncAction);
  }, [startChooseVariantsSync, startProductSync, startSingleVariantSync]);

  const startSync = useCallback(async (syncAction) => {
    let syncProduct = product;
    const productsState = store.getState().products;

    if (needsMasterVariantsFetch(productsState, productId)) {
      try {
        await dispatch(getMasterProduct({ productId })).unwrap();
      } catch {
        // getMasterProduct already toasts/logs
      }
      syncProduct = readMasterProductFromState(store.getState().products, productId);
    }

    const scenario = getSyncScenario({ variantId, product: syncProduct });

    runSyncScenario(scenario, syncProduct, syncAction);
  }, [dispatch, product, productId, runSyncScenario, store, variantId]);

  const onPublishDirectlyClick = useCallback(() => (
    startSync(CHANNEL_SYNC_ACTION.PUBLISH)
  ), [startSync]);

  const onCreateDraftsClick = useCallback(() => (
    startSync(CHANNEL_SYNC_ACTION.DRAFT)
  ), [startSync]);

  return {
    onPublishDirectlyClick,
    onCreateDraftsClick,
    isSyncDisabled,
    connectionLostTooltip,
    hasConnectedChannels,
    isBusy,
    busyLoadingText,
  };
};

export default useSyncToChannel;
